<template>
  <div class="short-text-card">
    <!-- ── Light stem: section label, title, optional one line, optional
         one image. Deliberately NOT the content card's full 2-image /
         2-text slot block — this slice uses a small dedicated stem
         (see docs/SPEC_short_text_and_number_question_cards.md). ── -->
    <div class="question-block">
      <p v-if="sectionLabel" class="section-label">{{ sectionLabel }}</p>
      <h1 class="question-title">{{ title }}</h1>
      <p v-if="prompt" class="question-prompt">{{ prompt }}</p>
    </div>

    <div v-if="imageId != null" class="stem-image-slot">
      <img
        v-if="imageRecord"
        class="stem-image"
        :src="imageUrl(imageRecord)"
        :alt="altTextFor(imageRecord, locale)"
        @error="onImgError($event, imageRecord, locale)"
      >
      <div v-else class="stem-image-placeholder">
        <span class="stem-image-icon" aria-hidden="true">🖼</span>
        <span class="stem-image-text">{{ dict.imagePlaceholder }} #{{ imageId }}</span>
      </div>
    </div>

    <!-- ── Input: single-line text honoring placeholder + max length. ── -->
    <div class="input-block">
      <input
        ref="input"
        type="text"
        class="text-input"
        :class="{ 'is-invalid': showError }"
        :value="value"
        :placeholder="placeholder"
        :maxlength="maxLength != null ? maxLength : undefined"
        :aria-invalid="showError ? 'true' : 'false'"
        :aria-describedby="showError ? errorId : undefined"
        autocomplete="off"
        @input="onInput"
        @blur="touched = true"
      >
      <div class="input-footer">
        <p v-if="showError" :id="errorId" class="input-error" role="alert">{{ errorMessage }}</p>
        <span v-else-if="required" class="input-hint">{{ dict.requiredHint }}</span>
        <span v-if="maxLength != null" class="input-counter" :class="{ 'is-invalid': showError && evaluation.error === 'too_long' }">
          {{ value.length }}/{{ maxLength }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
// ShortTextCard — runtime card for question_type = 'short_text'.
//
// Engine contract (docs/CONTRACT_CARD_ENGINE.md):
//   props : item, state
//   emits : state-changed   (no request-advance — free text must not
//                            auto-advance out from under a typing user)
//
// State shape: { value: <string> }. Emitted on every input, verbatim —
// partial data is valid data (the engine is a basket). Persistence, when
// it lands, reads state.value straight into seq_answers.value_text; this
// card already emits exactly what that path will consume.
//
// Question content shape (item.item.content):
//   title:    { de,en,fr,it }          stem title
//   prompt:   { de,en,fr,it }          optional one line under the title
//   image_id: number | null            optional single stem image
//   input: {
//     required:    boolean
//     max_length:  number | null
//     placeholder: { de,en,fr,it }
//     pattern:     string | null       (regex source)
//   }
//
// Validity is computed by the shared evaluateShortText() helper so the
// card's inline feedback and the engine's _isAnswered()/gating use one
// source of truth.

import i18n from '@/i18n';
import { imageUrl, altTextFor, onImgError } from '@/services/imageHelpers';
import { loadImageMap } from '@/services/imageApi';
import { formatSectionLabel } from '../sectionLabel.js';
import { shortTextConfig, evaluateShortText } from './shortTextValidation.js';

const DICT = {
  en: { imagePlaceholder: 'Image', requiredHint: 'Required', errRequired: 'This field is required.', errTooLong: 'Too long.', errPattern: 'Please match the requested format.' },
  de: { imagePlaceholder: 'Bild', requiredHint: 'Pflichtfeld', errRequired: 'Dieses Feld ist erforderlich.', errTooLong: 'Zu lang.', errPattern: 'Bitte das verlangte Format einhalten.' },
  fr: { imagePlaceholder: 'Image', requiredHint: 'Obligatoire', errRequired: 'Ce champ est obligatoire.', errTooLong: 'Trop long.', errPattern: 'Merci de respecter le format demandé.' },
  it: { imagePlaceholder: 'Immagine', requiredHint: 'Obbligatorio', errRequired: 'Questo campo è obbligatorio.', errTooLong: 'Troppo lungo.', errPattern: 'Rispetta il formato richiesto.' },
};

export default {
  name: 'ShortTextCard',
  props: {
    item: { type: Object, required: true },
    state: { type: [Object, null], default: null },
  },
  emits: ['state-changed'],
  data() {
    return {
      // Whether the field has been blurred at least once — error styling
      // stays quiet until then so a freshly-mounted required field isn't
      // already shouting before the user has typed anything.
      touched: false,
      imageMap: null,
    };
  },
  created() {
    if (this.imageId != null) {
      loadImageMap()
        .then((map) => { this.imageMap = map; })
        .catch(() => { /* leave the placeholder if the library can't load */ });
    }
  },
  computed: {
    content() {
      return (this.item && this.item.item && this.item.item.content) || {};
    },
    locale() {
      return i18n.global.locale.value;
    },
    dict() {
      return DICT[this.locale] || DICT.en;
    },
    config() {
      return shortTextConfig(this.item);
    },
    required() { return this.config.required; },
    maxLength() { return this.config.maxLength; },
    title() { return this._localised(this.content.title); },
    prompt() { return this._localised(this.content.prompt); },
    placeholder() { return this._localised(this.config.placeholder); },
    sectionLabel() { return formatSectionLabel(this.item, this.locale); },
    imageId() {
      const v = Number(this.content.image_id);
      return Number.isFinite(v) ? v : null;
    },
    imageRecord() {
      if (this.imageId == null || !this.imageMap) return null;
      return this.imageMap.get(this.imageId) || null;
    },
    value() {
      return (this.state && typeof this.state.value === 'string') ? this.state.value : '';
    },
    evaluation() {
      return evaluateShortText(this.value, this.config);
    },
    showError() {
      return this.touched && this.evaluation.error != null;
    },
    errorMessage() {
      switch (this.evaluation.error) {
        case 'required': return this.dict.errRequired;
        case 'too_long': return this.dict.errTooLong;
        case 'pattern': return this.dict.errPattern;
        default: return '';
      }
    },
    errorId() {
      return `short-text-error-${this.item ? this.item.id : 'x'}`;
    },
  },
  methods: {
    _localised(field) {
      if (!field || typeof field !== 'object') return '';
      const order = [this.locale, 'en', 'de', 'fr', 'it'];
      for (const loc of order) {
        const v = field[loc];
        if (typeof v === 'string' && v.trim()) return v.trim();
      }
      return '';
    },
    onInput(e) {
      // Full replacement, every keystroke. The engine stores it verbatim;
      // validity is the engine/card's concern, not a reason to withhold
      // the value.
      this.$emit('state-changed', { value: e.target.value });
    },
    // Exposed to the template for rendering the optional stem image.
    imageUrl,
    altTextFor,
    onImgError,
  },
};
</script>

<style scoped>
.short-text-card { font-family: inherit; }

.question-block { margin-bottom: 1.25rem; }
.section-label { font-size: 13px; color: #5f5e5a; margin: 0 0 6px; }
.question-title {
  font-size: 22px; font-weight: 500; margin: 0 0 8px;
  line-height: 1.25; color: #2c2c2a;
}
.question-prompt { font-size: 14px; color: #5f5e5a; margin: 0; }

.stem-image-slot { margin: 0 0 1.25rem; }
.stem-image {
  display: block; width: 100%; height: auto;
  border-radius: 8px; border: 0.5px solid rgba(0, 0, 0, 0.1);
}
.stem-image-placeholder {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 6px; height: 140px;
  border: 0.5px dashed rgba(0,0,0,0.18); border-radius: 8px;
  background: #fafaf7; color: #888780; font-size: 12px;
}
.stem-image-icon { font-size: 28px; line-height: 1; }

.input-block { margin-bottom: 1.25rem; }
.text-input {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  font-size: 16px;
  font-family: inherit;
  color: #2c2c2a;
  background: #ffffff;
  border: 0.5px solid rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.text-input:focus {
  outline: none;
  border-color: #534AB7;
  box-shadow: 0 0 0 3px rgba(83, 74, 183, 0.12);
}
.text-input.is-invalid {
  border-color: #C8392E;
}
.text-input.is-invalid:focus {
  box-shadow: 0 0 0 3px rgba(200, 57, 46, 0.12);
}
.text-input::placeholder { color: #b3b1a8; }

.input-footer {
  display: flex; align-items: baseline; justify-content: space-between;
  gap: 12px; margin-top: 6px; min-height: 16px;
}
.input-error { font-size: 12px; color: #C8392E; margin: 0; }
.input-hint { font-size: 12px; color: #888780; }
.input-counter { font-size: 12px; color: #b3b1a8; margin-left: auto; font-variant-numeric: tabular-nums; }
.input-counter.is-invalid { color: #C8392E; }
</style>
